import { useState } from 'react';
import { formatDisplayName } from '../../utils/nameUtils';
import { Link, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Button,
  Modal,
  Alert,
} from 'flowbite-react';
import { HiChevronRight, HiChevronDown, HiMail, HiPhone, HiUser, HiGlobe, HiPlus, HiPencil, HiTrash, HiRefresh } from 'react-icons/hi';
import { EmptyState } from '../common/EmptyState';
import { CreateStaffModal } from '../modals/CreateStaffModal';
import { ManageContactStatusModal } from '../modals/ManageContactStatusModal';
import { AddSchoolModal } from '../modals/AddSchoolModal';
import { EditSchoolModal } from '../modals/EditSchoolModal';
import { DeleteSchoolModal } from '../modals/DeleteSchoolModal';
import { AssignmentsManager } from '../staff/AssignmentsManager';
import { staffApi, schoolsApi } from '../../api';
import type { LEAResponse, SchoolSummary } from '../../types/api';

interface SchoolsTabProps {
  lea: LEAResponse;
  onUpdate: () => void;
  canEdit?: boolean;
  canAddSchool?: boolean;
  /** Whether the current user can delete schools (superAdmin only). */
  canDeleteSchool?: boolean;
  /** Whether Closed schools should be included in the list (page-level state). */
  showClosedSchools?: boolean;
  /** Setter for the show-closed-schools toggle. */
  onToggleShowClosedSchools?: (next: boolean) => void;
}

export const SchoolsTab: React.FC<SchoolsTabProps> = ({
  lea,
  onUpdate,
  canEdit = false,
  canAddSchool = false,
  canDeleteSchool = false,
  showClosedSchools = false,
  onToggleShowClosedSchools,
}) => {
  const navigate = useNavigate();
  const [expandedSchools, setExpandedSchools] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Staff creation modal state
  const [showCreateStaffModal, setShowCreateStaffModal] = useState(false);
  const [selectedSchoolForStaff, setSelectedSchoolForStaff] = useState<{ schoolId: number; educationOrganizationId: number; name: string } | null>(null);

  // Assignments manager modal state
  const [showManageAssignmentsModal, setShowManageAssignmentsModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<{ staffId: number; personId: number; personName: string; assignments: any[] } | null>(null);
  const [lockedOrganizationId, setLockedOrganizationId] = useState<number | null>(null);

  // Add school modal state
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);

  // Edit school modal state
  const [showEditSchoolModal, setShowEditSchoolModal] = useState(false);
  const [selectedSchoolForEdit, setSelectedSchoolForEdit] = useState<SchoolSummary | null>(null);

  // Delete school modal state
  const [showDeleteSchoolModal, setShowDeleteSchoolModal] = useState(false);
  const [selectedSchoolForDelete, setSelectedSchoolForDelete] = useState<SchoolSummary | null>(null);

  // Contact status modal state
  const [showManageStatusModal, setShowManageStatusModal] = useState(false);
  const [selectedPersonForStatus, setSelectedPersonForStatus] = useState<{ personId: number; staffId: number; name: string } | null>(null);

  const handleAddStaffToSchool = (school: { schoolId: number; educationOrganizationId: number; nameOfInstitution: string }) => {
    setSelectedSchoolForStaff({
      schoolId: school.schoolId,
      educationOrganizationId: school.educationOrganizationId,
      name: school.nameOfInstitution
    });
    setShowCreateStaffModal(true);
  };

  const handleStaffCreated = async (staffId: number, _personId: number) => {
    setShowCreateStaffModal(false);

    try {
      const fullStaff = await staffApi.get(staffId);
      setSelectedStaff({
        staffId: fullStaff.staffId,
        personId: fullStaff.person.personId,
        personName: formatDisplayName({ ...fullStaff.person, lastName: fullStaff.person.lastSurname }),
        assignments: fullStaff.assignments
      });
      setLockedOrganizationId(selectedSchoolForStaff?.educationOrganizationId || null);
      setShowManageAssignmentsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load staff member');
    }
  };

  const handleEditStaffAssignments = async (staffId: number, schoolEducationOrganizationId: number) => {
    try {
      const fullStaff = await staffApi.get(staffId);
      setSelectedStaff({
        staffId: fullStaff.staffId,
        personId: fullStaff.person.personId,
        personName: formatDisplayName({ ...fullStaff.person, lastName: fullStaff.person.lastSurname }),
        assignments: fullStaff.assignments
      });
      setLockedOrganizationId(schoolEducationOrganizationId);
      setShowManageAssignmentsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load staff member');
    }
  };

  const handleDeleteStaff = async (schoolId: number, assignmentId: number, staffName: string) => {
    if (!confirm(`Are you sure you want to remove ${staffName} from this school?`)) {
      return;
    }

    try {
      setError(null);
      await schoolsApi.removeStaff(schoolId, assignmentId);
      setSuccessMessage(`Successfully removed ${staffName}`);
      onUpdate();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to remove staff member');
    }
  };

  const handleUpdateAssignments = async () => {
    onUpdate();

    if (selectedStaff) {
      try {
        const updatedStaff = await staffApi.get(selectedStaff.staffId);
        setSelectedStaff({
          ...selectedStaff,
          assignments: updatedStaff.assignments
        });
      } catch (err) {
        console.error('Failed to refresh staff assignments:', err);
      }
    }
  };

  const toggleSchoolExpanded = (schoolId: number) => {
    const newExpanded = new Set(expandedSchools);
    if (newExpanded.has(schoolId)) {
      newExpanded.delete(schoolId);
    } else {
      newExpanded.add(schoolId);
    }
    setExpandedSchools(newExpanded);
  };

  const handleSchoolCreated = (_schoolId: number) => {
    setShowAddSchoolModal(false);
    setSuccessMessage('School created successfully');
    onUpdate();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleEditSchool = (school: SchoolSummary) => {
    setSelectedSchoolForEdit(school);
    setShowEditSchoolModal(true);
  };

  const handleSchoolUpdated = () => {
    setShowEditSchoolModal(false);
    setSelectedSchoolForEdit(null);
    setSuccessMessage('School updated successfully');
    onUpdate();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleDeleteSchool = (school: SchoolSummary) => {
    setSelectedSchoolForDelete(school);
    setShowDeleteSchoolModal(true);
  };

  const handleReopenSchool = async (school: SchoolSummary) => {
    if (!confirm(`Reopen ${school.nameOfInstitution}? Its operational status will be set back to Active.`)) {
      return;
    }
    try {
      setError(null);
      await schoolsApi.reopen(school.schoolId);
      setSuccessMessage(`Reopened ${school.nameOfInstitution}`);
      onUpdate();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reopen school');
    }
  };

  const handleSchoolDeleted = (kind: 'closed' | 'permanentlyDeleted') => {
    const name = selectedSchoolForDelete?.nameOfInstitution || 'School';
    setShowDeleteSchoolModal(false);
    setSelectedSchoolForDelete(null);
    setSuccessMessage(
      kind === 'permanentlyDeleted'
        ? `${name} permanently deleted`
        : `${name} closed`
    );
    onUpdate();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  if (!lea.schools || lea.schools.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<HiGlobe className="w-16 h-16 text-gray-400" />}
          title="No schools found"
          description="This organization doesn't have any schools yet."
          action={canAddSchool ? {
            label: 'Add School',
            onClick: () => setShowAddSchoolModal(true),
            icon: <HiPlus className="mr-2 h-4 w-4" />
          } : undefined}
        />

        {/* Add School Modal - for empty state */}
        {canAddSchool && (
          <AddSchoolModal
            isOpen={showAddSchoolModal}
            onClose={() => setShowAddSchoolModal(false)}
            onSuccess={handleSchoolCreated}
            leaId={lea.leaId}
            leaEducationOrganizationId={lea.educationOrganizationId}
            leaName={lea.nameOfInstitution}
          />
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {(() => {
            const directCount = lea.schools.filter(s => !s.isAssociated).length;
            const assocCount = lea.schools.filter(s => s.isAssociated).length;
            const base = `${directCount} school${directCount !== 1 ? 's' : ''} in this organization`;
            return assocCount > 0 ? `${base} (+${assocCount} associated)` : base;
          })()}
        </p>
        <div className="flex items-center gap-3">
          {canDeleteSchool && onToggleShowClosedSchools && (
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={showClosedSchools}
                onChange={(e) => onToggleShowClosedSchools(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
              />
              Show closed schools
            </label>
          )}
          {canAddSchool && (
            <Button
              size="sm"
              color="light"
              onClick={() => setShowAddSchoolModal(true)}
              className="text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"
            >
              <HiPlus className="mr-2 h-4 w-4" />
              Add School
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
      <Table hoverable={true} className="divide-y divide-gray-200 dark:divide-gray-700">
        <TableHead className="bg-gray-50 dark:bg-gray-800">
          <TableHeadCell className="w-10 px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider"></TableHeadCell>
          <TableHeadCell className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">School Name</TableHeadCell>
          <TableHeadCell className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Address</TableHeadCell>
          <TableHeadCell className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Principal</TableHeadCell>
          <TableHeadCell className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Grades</TableHeadCell>
          <TableHeadCell className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Phone</TableHeadCell>
          <TableHeadCell className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Email</TableHeadCell>
          <TableHeadCell className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Website</TableHeadCell>
          {canEdit && <TableHeadCell className="w-20 px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</TableHeadCell>}
        </TableHead>
        <TableBody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
          {lea.schools.map((school) => {
            const isExpanded = expandedSchools.has(school.schoolId);
            const hasStaff = school.staff && school.staff.length > 0;

            return (
              <>
                <TableRow
                  key={school.schoolId}
                  className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => toggleSchoolExpanded(school.schoolId)}
                >
                  <TableCell className="px-2">
                    <button
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? `Collapse ${school.nameOfInstitution} details` : `Expand ${school.nameOfInstitution} contact info`}
                    >
                      {isExpanded ? <HiChevronDown className="h-4 w-4 dark:text-gray-400" /> : <HiChevronRight className="h-4 w-4 dark:text-gray-400" />}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {school.nameOfInstitution}
                        {school.isAssociated && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300">
                            Associated
                          </span>
                        )}
                        {school.isClosed && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            Closed
                          </span>
                        )}
                      </div>
                      {school.shortNameOfInstitution && school.shortNameOfInstitution !== school.nameOfInstitution && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{school.shortNameOfInstitution}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{school.primaryAddress || '—'}</span>
                  </TableCell>
                  <TableCell className="dark:text-gray-300">
                    {school.principalName || <span className="text-gray-400 dark:text-gray-500 italic">Not assigned</span>}
                  </TableCell>
                  <TableCell className="dark:text-gray-300">{school.gradesServed || '—'}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {school.primaryPhone ? (
                      <a
                        href={`tel:${school.primaryPhone}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                      >
                        {school.primaryPhone}
                      </a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {school.mainEmailAddress ? (
                      <a
                        href={`mailto:${school.mainEmailAddress}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                      >
                        {school.mainEmailAddress}
                      </a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {school.webSite ? (
                      <a
                        href={school.webSite.startsWith('http') ? school.webSite : `https://${school.webSite}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                        title={school.webSite}
                      >
                        {school.webSite.length > 35
                          ? `${school.webSite.slice(0, 20)}...${school.webSite.slice(-12)}`
                          : school.webSite}
                      </a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => handleEditSchool(school)}
                          className="text-gray-600 hover:text-gray-700"
                          aria-label={`Edit ${school.nameOfInstitution}`}
                        >
                          <HiPencil className="h-4 w-4" />
                        </Button>
                        {canDeleteSchool && !school.isAssociated && school.isClosed && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => handleReopenSchool(school)}
                            className="text-green-600 hover:text-green-700"
                            aria-label={`Reopen ${school.nameOfInstitution}`}
                            title="Reopen school"
                          >
                            <HiRefresh className="h-4 w-4" />
                          </Button>
                        )}
                        {canDeleteSchool && !school.isAssociated && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => handleDeleteSchool(school)}
                            className="text-red-600 hover:text-red-700"
                            aria-label={`Delete ${school.nameOfInstitution}`}
                            title="Delete school"
                          >
                            <HiTrash className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>

                {/* Expandable staff section */}
                {isExpanded && (
                  <TableRow className="bg-gray-50 dark:bg-gray-900">
                    <TableCell colSpan={canEdit ? 8 : 7} className="p-0">
                      <div className="px-8 py-4 bg-gray-50 dark:bg-gray-900">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            School Staff ({school.staff?.length || 0})
                          </h4>
                          {canEdit && (
                            <Button
                              size="xs"
                              color="light"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddStaffToSchool(school);
                              }}
                              className="text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"
                            >
                              <HiPlus className="mr-1 h-3 w-3" />
                              Add Staff
                            </Button>
                          )}
                        </div>
                        {hasStaff ? (
                          <div className="grid gap-3">
                            {school.staff?.map((staff) => (
                              <div
                                key={staff.staffEdOrgAssignmentAssociationId}
                                className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-between"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-blue-50 dark:bg-blue-900 rounded-full">
                                    <HiUser className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <div>
                                    <Link
                                      to={`/persons/${staff.personId}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline text-left"
                                    >
                                      {staff.displayName || formatDisplayName(staff)}
                                    </Link>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">{staff.positionTitle}</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  {staff.email && (
                                    <a
                                      href={`mailto:${staff.email}`}
                                      className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <HiMail className="mr-1 h-4 w-4" />
                                      {staff.email}
                                    </a>
                                  )}
                                  {staff.phone && (
                                    <a
                                      href={`tel:${staff.phone}`}
                                      className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <HiPhone className="mr-1 h-4 w-4" />
                                      {staff.phone}
                                    </a>
                                  )}
                                  {canEdit && (
                                    <div className="flex items-center gap-1 ml-2">
                                      <Button
                                        size="xs"
                                        color="light"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (staff.staffId) {
                                            handleEditStaffAssignments(staff.staffId, school.educationOrganizationId);
                                          }
                                        }}
                                        className="text-gray-600 hover:text-gray-700"
                                        aria-label={`Edit ${staff.displayName || formatDisplayName(staff)}`}
                                      >
                                        <HiPencil className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="xs"
                                        color="light"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (staff.staffId) {
                                            setSelectedPersonForStatus({
                                              personId: staff.personId,
                                              staffId: staff.staffId,
                                              name: staff.displayName || formatDisplayName(staff)
                                            });
                                            setShowManageStatusModal(true);
                                          } else {
                                            // Fallback: direct delete if no staffId
                                            handleDeleteStaff(
                                              school.schoolId,
                                              staff.staffEdOrgAssignmentAssociationId,
                                              staff.displayName || formatDisplayName(staff)
                                            );
                                          }
                                        }}
                                        className="text-red-600 hover:text-red-700"
                                        aria-label={`Manage ${staff.displayName || formatDisplayName(staff)}`}
                                      >
                                        <HiTrash className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                            <HiUser className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
                            <p className="text-sm">No staff assigned to this school yet.</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Click "Add Staff" to assign administrators.</p>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>
      </div>

      {/* Create Staff Modal */}
      {canEdit && selectedSchoolForStaff && (
        <CreateStaffModal
          isOpen={showCreateStaffModal}
          onClose={() => {
            setShowCreateStaffModal(false);
            setSelectedSchoolForStaff(null);
          }}
          onSuccess={({ staffId, personId }) => handleStaffCreated(staffId, personId)}
          defaultOrganizationId={selectedSchoolForStaff.educationOrganizationId}
          modalTitle={`Add Staff to ${selectedSchoolForStaff.name}`}
        />
      )}

      {/* Manage Assignments Modal */}
      {selectedStaff && (
        <Modal
          show={showManageAssignmentsModal}
          onClose={() => {
            setShowManageAssignmentsModal(false);
            setSelectedStaff(null);
            setLockedOrganizationId(null);
          }}
          dismissible
          size="3xl"
        >
          <div className="flex flex-col max-h-[85vh]">
            {/* Header - fixed */}
            <div className="flex items-center justify-between p-6 pb-4 border-b flex-shrink-0">
              <h3 className="text-xl font-medium text-gray-900">
                Manage Assignments: {selectedStaff.personName}
              </h3>
              <button
                onClick={() => {
                  setShowManageAssignmentsModal(false);
                  setSelectedStaff(null);
                  setLockedOrganizationId(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Profile link hint */}
            <div className="px-6 pt-3 pb-0">
              <p className="text-sm text-gray-500">
                <a
                  href={`/persons/${selectedStaff.personId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowManageAssignmentsModal(false);
                    setSelectedStaff(null);
                    setLockedOrganizationId(null);
                    navigate(`/persons/${selectedStaff.personId}`);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Visit {selectedStaff.personName}'s profile
                </a>
                {' '}to edit contact information.
              </p>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AssignmentsManager
                staffId={selectedStaff.staffId}
                assignments={selectedStaff.assignments}
                onUpdate={handleUpdateAssignments}
                lockedOrganizationId={lockedOrganizationId || undefined}
              />
            </div>

            {/* Footer - fixed */}
            <div className="p-6 pt-4 border-t flex justify-end flex-shrink-0">
              <Button
                color="gray"
                onClick={() => {
                  setShowManageAssignmentsModal(false);
                  setSelectedStaff(null);
                  setLockedOrganizationId(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add School Modal */}
      {canAddSchool && (
        <AddSchoolModal
          isOpen={showAddSchoolModal}
          onClose={() => setShowAddSchoolModal(false)}
          onSuccess={handleSchoolCreated}
          leaId={lea.leaId}
          leaEducationOrganizationId={lea.educationOrganizationId}
          leaName={lea.nameOfInstitution}
        />
      )}

      {/* Edit School Modal */}
      {canEdit && selectedSchoolForEdit && (
        <EditSchoolModal
          isOpen={showEditSchoolModal}
          onClose={() => {
            setShowEditSchoolModal(false);
            setSelectedSchoolForEdit(null);
          }}
          onSuccess={handleSchoolUpdated}
          school={selectedSchoolForEdit}
        />
      )}

      {/* Delete School Modal (superAdmin only) */}
      {canDeleteSchool && selectedSchoolForDelete && (
        <DeleteSchoolModal
          isOpen={showDeleteSchoolModal}
          onClose={() => {
            setShowDeleteSchoolModal(false);
            setSelectedSchoolForDelete(null);
          }}
          onSuccess={handleSchoolDeleted}
          school={{
            schoolId: selectedSchoolForDelete.schoolId,
            nameOfInstitution: selectedSchoolForDelete.nameOfInstitution,
          }}
        />
      )}

      {/* Manage Contact Status Modal */}
      {selectedPersonForStatus && (
        <ManageContactStatusModal
          isOpen={showManageStatusModal}
          onClose={() => {
            setShowManageStatusModal(false);
            setSelectedPersonForStatus(null);
          }}
          personId={selectedPersonForStatus.personId}
          personName={selectedPersonForStatus.name}
          staffId={selectedPersonForStatus.staffId}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};